import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { LANGS, DEFAULT_LANG } from "@/lib/i18n";
import { DEFAULT_OG } from "@/lib/cdn";

// Centralized SEO component.
// - Generates <title>, description, canonical, Open Graph, Twitter, hreflang
//   alternates, and optional JSON-LD.
// - `image` defaults to the CDN default OG image when not provided.
// - `noindex` emits robots meta for search/filter/cookie pages.
export const SEO = ({
  title,
  description,
  canonical,
  image,
  type = "website",
  published,
  modified,
  author,
  jsonLd,
  jsonLdList,
  breadcrumbs,
  noindex = false,
  alternates, // [{ hreflang, href }]
}) => {
  const location = useLocation();
  const base = "geoVINUM";
  const hasSite = title && /geo\s*vinum/i.test(title);
  const fullTitle = title ? (hasSite ? title : `${title} — ${base}`) : `${base} — Understand wine visually`;

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const path = location?.pathname || "/";
  const autoCanonical = canonical || `${origin}${path}`;
  // Social crawlers (Facebook, Twitter, LinkedIn) require absolute image URLs.
  // We accept relative inputs like `/api/thumbnails/x.png` and prefix them
  // with the current origin so the same value works in <img> tags too.
  const rawImg = image || DEFAULT_OG;
  const imgUrl = /^https?:\/\//i.test(rawImg) ? rawImg : `${origin}${rawImg}`;

  const alt = alternates || buildDefaultAlternates(origin, path);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={autoCanonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* hreflang alternates */}
      {alt.map((a) => (
        <link key={a.hreflang} rel="alternate" hrefLang={a.hreflang} href={a.href} />
      ))}

      {/* Open Graph */}
      <meta property="og:site_name" content="geoVINUM" />
      <meta property="og:type" content={type} />
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={imgUrl} />
      <meta property="og:image:alt" content={title || "geoVINUM"} />
      <meta property="og:url" content={autoCanonical} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:see_also" content="https://www.instagram.com/geovinum.io" />
      <meta property="og:see_also" content="https://www.instagram.com/geovinumfrance" />
      <meta property="og:see_also" content="https://www.facebook.com/geovinum" />
      <meta property="og:see_also" content="https://www.facebook.com/geovinumfrance" />
      <meta property="og:see_also" content="https://fr.pinterest.com/geovinummaps/" />
      {published && <meta property="article:published_time" content={published} />}
      {modified && <meta property="article:modified_time" content={modified} />}
      {author && <meta property="article:author" content={author} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={imgUrl} />

      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
      {/* Additional JSON-LD blocks (Organization, FAQPage, Course, …).
          Kept as a separate array so pages can compose N schemas without
          wrapping them all in a graph. */}
      {Array.isArray(jsonLdList) && jsonLdList.filter(Boolean).map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
      {/* Breadcrumbs — emitted as BreadcrumbList schema whenever the page
          hands us `breadcrumbs=[{name, url}, …]`. The visible breadcrumb
          UI stays the responsibility of the page (accessibility + design). */}
      {Array.isArray(breadcrumbs) && breadcrumbs.length > 1 && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: breadcrumbs.map((b, idx) => ({
              "@type": "ListItem",
              position: idx + 1,
              name: b.name,
              item: b.url
                ? (/^https?:\/\//i.test(b.url) ? b.url : `${origin}${b.url}`)
                : undefined,
            })),
          })}
        </script>
      )}
    </Helmet>
  );
};

function buildDefaultAlternates(origin, path) {
  const trim = (p) => (p || "/").split("?")[0];
  const base = trim(path);
  const out = LANGS.map((l) => ({
    hreflang: l.code,
    href: `${origin}${base}${l.code === DEFAULT_LANG ? "" : `?lang=${l.code}`}`,
  }));
  out.push({ hreflang: "x-default", href: `${origin}${base}` });
  return out;
}

export function articleJsonLd(post) {
  if (!post) return null;
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.seo?.description || post.excerpt,
    image: post.image ? [post.image] : undefined,
    datePublished: post.seo?.article_published_time || post.date,
    dateModified: post.seo?.article_modified_time || post.modified || post.date,
    author: post.author ? { "@type": "Person", name: post.author.name } : undefined,
    publisher: {
      "@type": "Organization",
      name: "geoVINUM",
      logo: {
        "@type": "ImageObject",
        url: "https://customer-assets.emergentagent.com/job_e33c4909-1cdb-40b6-bbfc-eef1964c596a/artifacts/qhav103f_geoVINUM_logo_RVB_300ppi_transparent.png",
      },
    },
    mainEntityOfPage: post.seo?.canonical || post.link,
  };
}

export function productJsonLd(product, { url, imageUrl }) {
  if (!product) return null;
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.valueLine || product.tagline,
    image: imageUrl ? [imageUrl] : undefined,
    brand: { "@type": "Brand", name: "geoVINUM" },
    category: product.type || "map",
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "EUR",
      lowPrice: product.priceFrom,
      highPrice: product.priceTo,
      url,
      availability: "https://schema.org/InStock",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      reviewCount: "200",
    },
  };
}

export default SEO;

// ─────────────────────────────────────────────────────────────────────
// Shared Schema.org helpers — geoVINUM entity, FAQ page, course lists.
// Kept in this module so callers only import from one place and the
// helpers automatically share the branding constants above.
// ─────────────────────────────────────────────────────────────────────

const GEOVINUM_LOGO =
  "https://customer-assets.emergentagent.com/job_e33c4909-1cdb-40b6-bbfc-eef1964c596a/artifacts/qhav103f_geoVINUM_logo_RVB_300ppi_transparent.png";

const GEOVINUM_SAMEAS = [
  "https://www.instagram.com/geovinum.io",
  "https://www.instagram.com/geovinumfrance",
  "https://www.facebook.com/geovinum",
  "https://www.facebook.com/geovinumfrance",
  "https://fr.pinterest.com/geovinummaps/",
];

/**
 * `Organization` schema — the geoVINUM entity itself. Used on the
 * homepage and on /about; both pages need the LLM crawlers to see the
 * canonical description + logo + parent org (EtOH) in one place.
 */
export function geovinumOrganizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://geovinum.io/#organization",
    name: "geoVINUM",
    url: "https://geovinum.io/",
    logo: GEOVINUM_LOGO,
    description:
      "geoVINUM est une plateforme de cartographie viticole, de données et d'apprentissage du vin. Cartes, base d'entités (régions, appellations, cépages, producteurs), Academy, AI et intégration B2B.",
    parentOrganization: {
      "@type": "Organization",
      name: "EtOH",
      url: "https://etoh.io/",
    },
    sameAs: GEOVINUM_SAMEAS,
    knowsAbout: [
      "Cartographie viticole",
      "Géographie du vin",
      "Appellations viticoles",
      "Terroirs viticoles",
      "Cépages",
      "WSET Level 3",
      "WSET Level 4 (Diploma)",
      "WSET Level 5",
      "Sommellerie",
    ],
    areaServed: "Worldwide",
    email: "hello@geovinum.io",
  };
}

/**
 * `WebSite` schema — enables the sitelinks search box in Google and
 * gives LLMs the canonical site URL + search endpoint.
 */
export function geovinumWebSiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://geovinum.io/#website",
    url: "https://geovinum.io/",
    name: "geoVINUM",
    publisher: { "@id": "https://geovinum.io/#organization" },
    inLanguage: ["fr", "en", "es", "pt"],
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: "https://geovinum.io/search?q={search_term_string}",
      },
      "query-input": "required name=search_term_string",
    },
  };
}

/**
 * `WebApplication` schema — used on the homepage to describe the
 * geoVINUM app itself (Maps + Explorer + Academy + AI stack).
 */
export function geovinumWebApplicationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "geoVINUM",
    url: "https://geovinum.io/",
    applicationCategory: "EducationApplication",
    operatingSystem: "Web, iOS, Android (PWA)",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "EUR",
      description:
        "Utilisation gratuite pour Explorer et les fiches. Certains modules (Academy, Maps téléchargeables) sont payants.",
    },
    publisher: { "@id": "https://geovinum.io/#organization" },
    featureList: [
      "Cartes viticoles imprimables et téléchargeables",
      "Explorer : base éditoriale et graphique des appellations, cépages, producteurs",
      "Academy : parcours d'apprentissage alignés WSET",
      "AI : assistant conversationnel spécialisé vin",
      "Cartes interactives intégrables (iframe) pour B2B",
    ],
  };
}

/**
 * `FAQPage` schema — pass a list of `{q, a}` items (a can be HTML,
 * we ship it as-is to LLMs).
 */
export function faqPageJsonLd(items) {
  if (!Array.isArray(items) || !items.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };
}

